import React, { useEffect, useMemo, useState } from 'react'
import InfoButton from '../../common/InfoButton'
import { useTranslation } from 'react-i18next'
import { useHasStudentInPath, useQuery } from '../../../utils/util'
import { classEmotionalWellbeing } from '../../../constants/StudentAnalyticsConstants'
import { LoadingContainer } from '../student/CheckInWidget'
import ProgressBarAnalytics from '../student/ProgressBarAnalytics'
import ExpandButton from '../../common/ExpandButton'
import { ReactComponent as Happy } from '../../../assets/svg/GreenSmiley.svg'
import { ReactComponent as Sad } from '../../../assets/svg/RedSad.svg'
import { ReasonIcons } from '../../../constants/Constants'
import { ReactComponent as ChevronDownDuoIcon } from '../../../assets/svg/ChevronDownDuoIcon.svg'
import { loadClassWellbeingByStudents } from '../../../server/server'
import { useTourContext } from '../../../context/TourContext'
import { calculateDateRange } from '../../../utils/dateUtils'

const SHOW_ROWS = 5
const STEP = 10

function ClassEmotionalWellbeing({ daysFilter, isPrimaryClass }) {
    const { t } = useTranslation()
    const query = useQuery()
    const currentId = query.get('id')
    const isStudentAnalytics = useHasStudentInPath()
    const [limit, setLimit] = useState(SHOW_ROWS)
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(null)
    const [percentage, setPercentage] = useState(0)
    const [total, setTotal] = useState(0)
    const showLoading = useMemo(() => loading || !data || !data?.length, [loading, data])
    const [hasMore, setHasMore] = useState(true)
    const [loadedAll, setLoadedAll] = useState(false)
    const [showMore, setShowMore] = useState(false)

    const { tourOpen } = useTourContext()

    useEffect(() => {
        setLoading(true)
        loadClassData(0, SHOW_ROWS)
        setLoadedAll(false)
        setShowMore(false)
        setLimit(SHOW_ROWS)
    }, [daysFilter, currentId])

    const loadClassData = async (loaded, limit = SHOW_ROWS) => {
        const { startDate, endDate } = calculateDateRange(daysFilter)
        const resp = await loadClassWellbeingByStudents(currentId, startDate, endDate, loaded, limit)
        if (resp.status === 200) {
            const respData = resp.data
            const previousResults = !loaded ? [] : [...(data ?? [])]
            setData([...previousResults, ...(respData?.results ?? [])])
            setTotal(respData?.total)
            setHasMore(respData?.total > respData?.results?.length)
            if (limit === respData?.total) {
                setLoadedAll(true)
            }
        }

        setLoading(false)
    }

    const handleShowMore = () => {
        setShowMore((prev) => !prev)
        if (!loadedAll) {
            if (!showMore) {
                loadClassData(data?.length, total)
                setLimit(total)
            }
        } else {
            if (showMore) {
                setLimit(SHOW_ROWS)
            } else {
                setLimit(total)
            }
        }
    }
    return (
        <div className="bg-white rounded-[24px] p-6 mt-4" data-tour="class-emotional-wellbeing-by-student">
            <div className=" flex flex-row items-center justify-between ">
                <div className="flex flex-row items-center gap-2">
                    <p className="text-[18px] leading-[25px] font-medium text-darkenPurple ">{t('Class Emotional Wellbeing by Student')}</p>
                    <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px] ">
                        <p className="text-[13px] leading-[18px] font-normal text-strongGray ">{t(daysFilter.label)}</p>
                    </div>
                </div>
                <button>
                    <InfoButton
                        color="#5E639B"
                        text={
                            isStudentAnalytics
                                ? t(
                                      'This section displays the activities created for the student during the selected period. You can remind the student to complete activities or view detailed information about each activity.'
                                  )
                                : t('This section displays key factors impacting each student’s emotional wellbeing, with a breakdown of comfortable and uncomfortable emotions.')
                        }
                    />
                </button>
            </div>
            <div className="relative">
                <DataContainer
                    limit={limit}
                    data={data}
                    blur={showLoading}
                    percentage={percentage}
                    setLimit={setLimit}
                    isStudentAnalytics={isStudentAnalytics}
                    isPrimaryClass={isPrimaryClass}
                />
                {showLoading && !tourOpen && <LoadingContainer showEmptyData={!loading && (!data || !data?.length)} />}
            </div>
            <div className="flex flex-row justify-end w-full mt-4">
                {!loading && hasMore && (
                    <button
                        className="flex flex-row items-center justify-center gap-2 border border-[#ECEBF8] hover:border-darkenPurple transition rounded-full h-[34px] px-4 bg-[#F8F8FB]"
                        onClick={() => handleShowMore()}
                    >
                        <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{limit > SHOW_ROWS ? t('Show less students') : t('See all students')}</p>
                        <div className={`${limit > SHOW_ROWS ? 'rotate-180' : 'rotate-0'} transition`}>
                            <ChevronDownDuoIcon />
                        </div>
                    </button>
                )}
            </div>
        </div>
    )
}

export default ClassEmotionalWellbeing

const DataContainer = ({ data, limit, isPrimaryClass }) => {
    const { tourOpen } = useTourContext()
    const maxValues = data?.reduce(
        (acc, obj) => {
            acc.maxComfortable = Math.max(acc?.maxComfortable, obj?.comfortable)
            acc.maxUncomfortable = Math.max(acc?.maxUncomfortable, obj?.uncomfortable)
            return acc
        },
        { maxComfortable: 0, maxUncomfortable: 0 }
    )
    const maxValue = Math.max(maxValues?.maxComfortable ?? 1, maxValues?.maxUncomfortable ?? 1)
    const intervalNumbers = Math.ceil((maxValue + 2) / STEP) + 1
    return (
        <div className="mt-4 pb-8">
            <div className="relative flex-1 z-0">
                <div className="absolute h-full inset-0 flex flex-row justify-between " style={{ zIndex: 1 }}>
                    <div className="w-[150px]"></div>
                    <div className="flex-1 flex flex-row justify-between">
                        {Array(intervalNumbers)
                            ?.fill(0)
                            ?.map((item, index) => (
                                <div id="dashed-vertical" className="h-full border-r border-dashed border-[#ECEBF8] relative " key={index}>
                                    <span
                                        className="absolute top-full mt-4 text-[12px] leading-5 text-[#77768D] "
                                        style={{
                                            transform: 'translateX(-50%)',
                                        }}
                                    >
                                        {index * 10}
                                    </span>
                                </div>
                            ))}
                    </div>
                    <div className="w-[41px]  "></div>
                </div>
                <div className="w-full relative flex flex-col gap-4" style={{ zIndex: 2 }}>
                    {data?.slice(0, limit).map((item, index) => (
                        <SingleItem
                            key={item.student.id ?? index}
                            item={item}
                            intervalNumbers={intervalNumbers}
                            isPrimaryClass={isPrimaryClass}
                            initialOpen={index === 0 && tourOpen}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}

const SingleItem = ({ item, intervalNumbers, isPrimaryClass, initialOpen }) => {
    const [open, setOpen] = useState(initialOpen)
    const percentage_comfortable = Math.round((item.comfortable / (intervalNumbers - 1)) * 10)
    const percentage_uncomfortable = Math.round((item.uncomfortable / (intervalNumbers - 1)) * 10)
    const total = item.comfortable + item.uncomfortable
    const percentageComfortableNumber = total != 0 ? Math.round((item.comfortable / total) * 100) : 0
    const percentageUncomfortableNumber = total != 0 ? Math.round((item.uncomfortable / total) * 100) : 0
    const { t } = useTranslation()

    useEffect(() => {
        setOpen(initialOpen)
    }, [initialOpen])

    return (
        <div className="flex flex-row gap-[10px] " data-tour="class-emotional-wellbeing-by-student-single_item">
            <div className="w-[140px] ">
                <div className="flex flex-row items-center gap-2 h-[35px]">
                    <div className="min-w-7 max-w-7 h-7 rounded-full bg-[#B8B8D6] flex items-center justify-center">
                        <p className="text-[12px] leading-[20px] font-medium text-[#000000] ">
                            {item.student?.first_name[0]}
                            {item.student?.last_name[0]}
                        </p>
                    </div>
                    <p className="text-[14px] leading-[20px] font-medium text-darkenPurple ">
                        {item.student?.first_name} {item.student?.last_name}
                    </p>
                </div>
            </div>
            <div className="flex-1 bg-[#F8F8FB] rounded-[4px] p-[2px]">
                <div className="flex-1 flex flex-row gap-[15px]">
                    <div className="flex flex-col gap-[1px] h-[31px]  flex-1">
                        <div className="flex-1 ">
                            <ProgressBarComfortable
                                frontColor={`#6FCC8799`}
                                restColor={`#F8F8FB`}
                                completed={percentage_comfortable}
                                percentage={percentageComfortableNumber}
                                value={item.comfortable}
                            />
                        </div>
                        <div className="flex-1 py-[3px] ">
                            <ProgressBarComfortable
                                height={4}
                                frontColor={`#FC4C5999`}
                                restColor={`#F8F8FB`}
                                completed={percentage_uncomfortable}
                                roundedLeft={1}
                                roundedRight={2}
                                value={item.uncomfortable}
                                percentage={percentageUncomfortableNumber}
                                comfortable={false}
                            />
                        </div>
                    </div>
                    {!isPrimaryClass && (
                        <div className="w-[26px]   pt-[4px] ">
                            <ExpandButton
                                open={open}
                                onClick={() => {
                                    setOpen((prev) => !prev)
                                }}
                                width={24}
                                height={24}
                            />
                        </div>
                    )}
                </div>

                <div className={`grid grid-cols-2 overflow-hidden transition-all duration-300 ${open ? 'max-h-[1000px] h-auto' : 'max-h-0'} ${open ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="flex flex-col bg-[#F8F8FB] p-3 ">
                        <div className="flex flex-row items-center">
                            <div className="flex items-center justify-center rounded-full w-8 h-8 bg-[#6FCC8733] ">
                                <Happy height={32} width={32} />
                            </div>
                            <p className="text-darkenPurple text-[14px] leading-5 font-semibold ml-[10px]  flex-1">{t('Comfortable Emotions')}</p>
                            <p className="text-[#6FCC87] text-[16px] leading-5 font-semibold ml-2">{item.comfortable}</p>
                        </div>
                        <div className="w-full h-[1px] bg-[#ECEBF8] mt-[10px]"></div>
                        <div className="flex flex-col gap-[10px] mt-[10px]">
                            {item?.comfortableReasons?.map((reason, index) => {
                                return <ReasonItem key={index} reason={reason} item={item} />
                            })}
                        </div>
                    </div>
                    <div className="flex flex-col bg-[#F8F8FB] p-3 ">
                        <div className="flex flex-row items-center">
                            <div className="flex items-center justify-center rounded-full w-8 h-8 bg-[#6FCC8733] ">
                                <Sad height={32} width={32} />
                            </div>
                            <p className="text-darkenPurple text-[14px] leading-5 font-semibold ml-[10px]  flex-1">{t('Uncomfortable Emotions')}</p>
                            <p className="text-[#FC4C5999] text-[16px] leading-5 font-semibold ml-2">{item.uncomfortable}</p>
                        </div>
                        <div className="w-full h-[1px] bg-[#ECEBF8] mt-[10px]"></div>
                        <div className="flex flex-col gap-[10px] mt-[10px]">
                            {item?.uncomfortableReasons?.map((reason, index) => {
                                return <ReasonItem key={index} reason={reason} item={item} isComfortable={false} />
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export const ProgressBarComfortable = ({
    height = 20,
    completed,
    frontColor = '#6FCC8799',
    restColor = '#FC4C5999',
    roundedLeft = 2,
    roundedRight = 4,
    value = 0,
    comfortable = true,
    showPercentage = false,
    percentage = 0,
}) => {
    return (
        <div
            className={`w-full flex flex-row  items-center gap-1`}
            style={{
                height: height,
                borderTopRightRadius: roundedRight,
                borderBottomRightRadius: roundedRight,
                borderTopLeftRadius: roundedLeft,
                borderBottomLeftRadius: roundedLeft,
            }}
        >
            <div
                style={{
                    width: `${Math.min(!!completed ? completed : 0.5, 100)}%`,
                    backgroundColor: frontColor,
                    height: height,
                    transition: 'width 400ms ease-in-out',
                    borderTopRightRadius: roundedRight,
                    borderBottomRightRadius: roundedRight,
                    borderTopLeftRadius: roundedLeft,
                    borderBottomLeftRadius: roundedLeft,
                }}
            ></div>
            <p className="text-nowrap font-semibold tracking-tight" style={{ fontSize: comfortable ? 12 : 10, color: comfortable ? '#389E52' : '#FC4C5999' }}>
                {value} {showPercentage && `(${percentage}%)`}
            </p>
        </div>
    )
}

const ReasonItem = ({ reason, item, isComfortable = true }) => {
    const { t } = useTranslation()
    const total = isComfortable ? item.comfortable : item.uncomfortable

    const percentage = Math.round((reason.count / total) * 100)

    return (
        <div>
            <div className="flex flex-row items-center justify-between flex-1">
                <div className="flex flex-row items-center">
                    <div className="flex items-center justify-center rounded-full w-8 h-8 bg-[#BEBECE33] ">{ReasonIcons(reason?.name.toLowerCase())}</div>

                    <p className="text-darkenPurple text-[14px] leading-5 font-medium ml-[10px]  w-[100px] capitalize">{t(reason?.name)}</p>
                </div>
                <div className="flex flex-row items-center flex-1">
                    <div className="flex-1">
                        <ProgressBarAnalytics
                            frontColor={isComfortable ? `#6FCC87` : '#FC4C5999'}
                            restColor={isComfortable ? `#6FCC8799` : '#FD949B99'}
                            completed={percentage}
                            height={6}
                        />
                    </div>
                </div>
                <p className="text-darkenPurple text-[16px] leading-5 font-semibold ml-1 text-right w-[40px]">{reason.count}</p>
            </div>
        </div>
    )
}
